'use client';
import { useState, useEffect, useRef } from 'react';
import { 
  MessageSquare, Send, Image as ImageIcon, X, User, RefreshCw, AtSign, 
  Users, Shield, Sparkles, Search, Camera, Phone, Video, PhoneOff, 
  Mic, MicOff, VideoOff, ChevronLeft, PhoneIncoming, Check, CheckCheck, Volume2, VolumeX, Bell 
} from 'lucide-react';

const DEFAULT_TEAM_USERS = [
  { username: 'admin', name: 'admin (ผู้ดูแลระบบ)', role: 'admin' },
  { username: 'navy', name: 'navy', role: 'user' },
  { username: 'DAMIRUN', name: 'DAMIRUN', role: 'user' },
  { username: 'nawin', name: 'nawin', role: 'user' },
  { username: 'sales', name: 'sales (ฝ่ายขาย)', role: 'sales' },
  { username: 'purchase', name: 'purchase (ฝ่ายจัดซื้อ)', role: 'purchases' },
  { username: 'tech', name: 'tech (ช่างเทคนิค)', role: 'tech' },
  { username: 'USER2', name: 'USER2', role: 'user' },
  { username: 'account', name: 'account (ฝ่ายบัญชี)', role: 'accounting' },
];

const RTC_CONFIG: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' }
  ]
};

export default function ChatPage() {
  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [textInput, setTextInput] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>({ name: 'ผู้ใช้งานระบบ', username: 'user' });
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [showKpiModal, setShowKpiModal] = useState<boolean>(false);
  const [nowTick, setNowTick] = useState<number>(Date.now());

  // User Selection, Online Status & Avatar System State
  const [userList, setUserList] = useState<any[]>(DEFAULT_TEAM_USERS);
  const [isUserScrolledUp, setIsUserScrolledUp] = useState<boolean>(false);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const [userAvatars, setUserAvatars] = useState<Record<string, string>>({});
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
  const [selectedRecipient, setSelectedRecipient] = useState<string>('ALL');
  const [searchUserQuery, setSearchUserQuery] = useState<string>('');
  const [mobileTab, setMobileTab] = useState<'users' | 'chat'>('users');
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);

  // Incoming & Active Call System State
  const [outgoingCallModal, setOutgoingCallModal] = useState<{ open: boolean; type: 'voice' | 'video'; targetUser: string } | null>(null);
  const [incomingCall, setIncomingCall] = useState<{ callerName: string; callerUsername: string; type: 'voice' | 'video'; messageId?: number } | null>(null);
  const [activeCallSession, setActiveCallSession] = useState<{ peerName: string; peerUsername: string; type: 'voice' | 'video' } | null>(null);
  const [callTimer, setCallTimer] = useState<number>(0);
  const [micPermissionGranted, setMicPermissionGranted] = useState<boolean>(false);
  const [audioConnected, setAudioConnected] = useState<boolean>(false);

  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  
  const handledCallsRef = useRef<Set<string>>(new Set());
  const prevMsgCountRef = useRef<number>(0);
  
  // WebRTC Audio Stream Refs
  const localStreamRef = useRef<MediaStream | null>(null);
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  // Mention system state
  const [showMentionMenu, setShowMentionMenu] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);


  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 15000);
    return () => clearInterval(t);
  }, []);

  const formatDurationShort = (sec: number) => {
    if (!sec || sec < 0) sec = 0;
    const mins = Math.floor(sec / 60);
    const remSec = Math.floor(sec % 60);
    if (mins === 0) return `${remSec} วินาที`;
    if (mins < 60) return `${mins} นาที`;
    const hrs = Math.floor(mins / 60);
    const remMins = mins % 60;
    return `${hrs} ชม. ${remMins} น.`;
  };

  // Extract target user for a message (from receiver_username or @mention)
  const getMessageTargetUser = (msg: any): string | null => {
    if (!msg) return null;
    const text = (msg.message || '');
    if (text.includes('📞') || text.includes('📹') || text.includes('กดรับสาย') || text.includes('จบการสนทนา') || text.includes('ไม่สะดวกรับสาย')) {
      return null;
    }
    if (msg.receiver_username && msg.receiver_username !== 'ALL') {
      return msg.receiver_username;
    }
    const match = text.match(/@([\wก-๙]+)/);
    if (match && match[1] && match[1].toLowerCase() !== 'all') {
      return match[1];
    }
    return null;
  };

  // Compute KPI stats per user across all messages
  const getUserKpiStats = (username: string) => {
    const uLower = (username || '').toLowerCase();
    let totalAssigned = 0;
    let fastCount = 0;
    let normalCount = 0;
    let slowCount = 0;
    let pendingCount = 0;
    let pendingOverdueCount = 0;
    let maxPendingSeconds = 0;
    let totalResponseSec = 0;
    let repliedCount = 0;

    messages.forEach((m, i) => {
      const senderLower = (m.sender_username || m.sender_name || '').toLowerCase();
      if (senderLower === uLower) return; // Ignore own messages

      const target = getMessageTargetUser(m);
      if (!target || target.toLowerCase() !== uLower) return;

      totalAssigned++;

      // Check if replied via DB column or subsequent message from this user
      let respSec = m.response_seconds ? Number(m.response_seconds) : null;
      if (respSec === null) {
        const msgTime = new Date(m.created_at).getTime();
        const nextReply = messages.slice(i + 1).find(r => {
          const rSender = (r.sender_username || r.sender_name || '').toLowerCase();
          return rSender === uLower && new Date(r.created_at).getTime() >= msgTime;
        });
        if (nextReply) {
          respSec = Math.max(1, Math.floor((new Date(nextReply.created_at).getTime() - msgTime) / 1000));
        }
      }

      if (respSec !== null) {
        repliedCount++;
        totalResponseSec += respSec;
        if (respSec <= 300) fastCount++;
        else if (respSec <= 900) normalCount++;
        else slowCount++;
      } else {
        pendingCount++;
        const waitSec = m.waiting_seconds !== undefined
          ? Number(m.waiting_seconds)
          : Math.max(0, Math.floor((nowTick - new Date(m.created_at).getTime()) / 1000));
        if (waitSec > maxPendingSeconds) maxPendingSeconds = waitSec;
        if (waitSec > 900) {
          pendingOverdueCount++;
          slowCount++;
        }
      }
    });

    const avgSec = repliedCount > 0 ? Math.round(totalResponseSec / repliedCount) : 0;
    // KPI Score: Start at 100%, Fast=100, Normal=80, Slow/Overdue=40
    let kpiScore = 100;
    if (totalAssigned > 0) {
      const completedFast = fastCount * 100;
      const completedNormal = normalCount * 80;
      const penalizedSlow = slowCount * 40;
      const inProgressOnTime = (pendingCount - pendingOverdueCount) * 90;
      kpiScore = Math.max(0, Math.min(100, Math.round((completedFast + completedNormal + penalizedSlow + inProgressOnTime) / totalAssigned)));
    }

    const grade = kpiScore >= 90 ? 'A' : kpiScore >= 75 ? 'B' : kpiScore >= 60 ? 'C' : 'D';
    return {
      totalAssigned,
      repliedCount,
      fastCount,
      normalCount,
      slowCount,
      pendingCount,
      pendingOverdueCount,
      maxPendingSeconds,
      avgSec,
      kpiScore,
      grade
    };
  };

  // Online Presence Heartbeat System
  useEffect(() => {
    if (!currentUser || !currentUser.username) return;

    const sendHeartbeat = async () => {
      const activeDb = localStorage.getItem('active_company') || 'express_db';
      try {
        await fetch(`/api/chat/presence?db=${activeDb}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: currentUser.username })
        });
        const res = await fetch(`/api/chat/presence?db=${activeDb}`);
        const json = await res.json();
        if (json.success && Array.isArray(json.onlineUsers)) {
          setOnlineUsers(json.onlineUsers);
        }
      } catch (e) {}
    };

    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 10000);
    return () => clearInterval(interval);
  }, [currentUser.username]);

  // WebRTC BroadcastChannel & Signaling Engine
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const channel = new BroadcastChannel('express_webrtc_voice_channel');
      broadcastChannelRef.current = channel;

      channel.onmessage = async (event) => {
        const data = event.data;
        if (!data || !currentUser || !currentUser.username) return;

        const myUser = currentUser.username.toLowerCase();
        const targetUser = (data.targetUser || '').toLowerCase();

        if (targetUser === myUser) {
          if (data.type === 'offer' && data.sdp) {
            handleReceivedOffer(data.sdp, data.fromUser);
          } else if (data.type === 'answer' && data.sdp && peerConnectionRef.current) {
            try {
              await peerConnectionRef.current.setRemoteDescription(new RTCSessionDescription(data.sdp));
              setAudioConnected(true);
            } catch (err) {}
          } else if (data.type === 'ice' && data.candidate && peerConnectionRef.current) {
            try {
              await peerConnectionRef.current.addIceCandidate(new RTCIceCandidate(data.candidate));
            } catch (err) {}
          }
        }
      };

      return () => {
        channel.close();
      };
    } catch (e) {}
  }, [currentUser.username]);

  // New Message Arrival Sound Chime Synthesizer
  const playNewMessageSound = () => {
    try {
      const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtxClass) return;
      const ctx = new AudioCtxClass();
      if (ctx.state === 'suspended') ctx.resume();

      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(1046.5, ctx.currentTime);
      gain1.gain.setValueAtTime(0.15, ctx.currentTime);
      gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(ctx.currentTime);
      osc1.stop(ctx.currentTime + 0.3);

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(1318.5, ctx.currentTime + 0.1);
      gain2.gain.setValueAtTime(0.18, ctx.currentTime + 0.1);
      gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(ctx.currentTime + 0.1);
      osc2.stop(ctx.currentTime + 0.5);
    } catch (e) {}
  };

  // Ringtone Audio Synthesizer via Web Audio API for Incoming Calls
  useEffect(() => {
    if (!incomingCall) return;

    let isPlaying = true;
    let audioCtx: AudioContext | null = null;
    let intervalId: any = null;

    try {
      const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioCtxClass) {
        audioCtx = new AudioCtxClass();

        const playRingBurst = () => {
          if (!isPlaying || !audioCtx) return;
          try {
            if (audioCtx.state === 'suspended') {
              audioCtx.resume();
            }

            const osc1 = audioCtx.createOscillator();
            const osc2 = audioCtx.createOscillator();
            const gain = audioCtx.createGain();

            osc1.type = 'sine';
            osc2.type = 'sine';

            osc1.frequency.setValueAtTime(440, audioCtx.currentTime);
            osc2.frequency.setValueAtTime(480, audioCtx.currentTime);

            gain.gain.setValueAtTime(0.2, audioCtx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 1.6);

            osc1.connect(gain);
            osc2.connect(gain);
            gain.connect(audioCtx.destination);

            osc1.start();
            osc2.start();
            osc1.stop(audioCtx.currentTime + 1.6);
            osc2.stop(audioCtx.currentTime + 1.6);
          } catch (err) {}
        };

        playRingBurst();
        intervalId = setInterval(playRingBurst, 2500);
      }
    } catch (e) {}

    return () => {
      isPlaying = false;
      if (intervalId) clearInterval(intervalId);
      if (audioCtx) {
        try { audioCtx.close(); } catch (e) {}
      }
    };
  }, [incomingCall]);

  // Clean WebRTC Call Setup Engine
  const setupWebRTCPeerConnection = async (targetUser: string, isCaller: boolean) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;
      setMicPermissionGranted(true);

      const pc = new RTCPeerConnection(RTC_CONFIG);
      peerConnectionRef.current = pc;

      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      pc.ontrack = (event) => {
        if (remoteAudioRef.current && event.streams && event.streams[0]) {
          remoteAudioRef.current.srcObject = event.streams[0];
          remoteAudioRef.current.play().then(() => setAudioConnected(true)).catch(() => {});
        }
      };

      pc.onicecandidate = (event) => {
        if (event.candidate && broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({
            type: 'ice',
            candidate: event.candidate,
            fromUser: currentUser.username,
            targetUser: targetUser
          });
        }
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'connected') {
          setAudioConnected(true);
        }
      };

      if (isCaller) {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({
            type: 'offer',
            sdp: offer,
            fromUser: currentUser.username,
            targetUser: targetUser
          });
        }
      }
    } catch (err: any) {
      console.error('WebRTC Setup Error:', err);
    }
  };

  const handleReceivedOffer = async (offerSdp: RTCSessionDescriptionInit, callerUser: string) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStreamRef.current = stream;
      setMicPermissionGranted(true);

      const pc = new RTCPeerConnection(RTC_CONFIG);
      peerConnectionRef.current = pc;

      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      pc.ontrack = (event) => {
        if (remoteAudioRef.current && event.streams && event.streams[0]) {
          remoteAudioRef.current.srcObject = event.streams[0];
          remoteAudioRef.current.play().then(() => setAudioConnected(true)).catch(() => {});
        }
      };

      pc.onicecandidate = (event) => {
        if (event.candidate && broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage({
            type: 'ice',
            candidate: event.candidate,
            fromUser: currentUser.username,
            targetUser: callerUser
          });
        }
      };

      await pc.setRemoteDescription(new RTCSessionDescription(offerSdp));
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({
          type: 'answer',
          sdp: answer,
          fromUser: currentUser.username,
          targetUser: callerUser
        });
      }
      setAudioConnected(true);
    } catch (err) {
      console.error('Handle Received Offer Error:', err);
    }
  };

  const stopWebRTCCall = () => {
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
    setAudioConnected(false);
  };

  const toggleMicMute = () => {
    const nextMute = !isMuted;
    setIsMuted(nextMute);
    if (localStreamRef.current) {
      localStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = !nextMute;
      });
    }
  };

  // Call Timer Counter
  useEffect(() => {
    let interval: any = null;
    if (activeCallSession) {
      interval = setInterval(() => {
        setCallTimer(prev => prev + 1);
      }, 1000);
    } else {
      setCallTimer(0);
    }
    return () => clearInterval(interval);
  }, [activeCallSession]);

  // Visual Viewport Listener for Mobile Keyboard height adaptation
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;
    
    const handleViewportResize = () => {
      if (window.visualViewport) {
        setViewportHeight(window.visualViewport.height);
      }
    };

    handleViewportResize();
    window.visualViewport.addEventListener('resize', handleViewportResize);
    window.visualViewport.addEventListener('scroll', handleViewportResize);

    return () => {
      window.visualViewport?.removeEventListener('resize', handleViewportResize);
      window.visualViewport?.removeEventListener('scroll', handleViewportResize);
    };
  }, []);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const u = JSON.parse(userStr);
        setCurrentUser({
          name: u.name || u.description || u.username || 'ผู้ใช้งานระบบ',
          username: u.username || u.name || 'user'
        });
      } catch (e) {}
    }

    fetchMessages();
    fetchUsers();
    fetchAvatars();

    const interval = setInterval(() => {
      fetchMessages(true);
    }, 4000);

    return () => clearInterval(interval);
  }, [currentUser.username, selectedRecipient]);

  useEffect(() => {
    setIsUserScrolledUp(false);
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [selectedRecipient, mobileTab]);

  useEffect(() => {
    if (!isUserScrolledUp) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const scrollToBottom = (force = false) => {
    if (force || !isUserScrolledUp) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleChatScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isBottom = scrollHeight - scrollTop - clientHeight < 100;
    setIsUserScrolledUp(!isBottom);
  };

  const handleSelectUserChannel = (username: string) => {
    setSelectedRecipient(username);
    setMobileTab('chat');
    markMessagesAsRead(username);
  };

  const markMessagesAsRead = async (senderUser = '') => {
    if (!currentUser || !currentUser.username || !senderUser || senderUser === 'ALL') return;

    const targetLower = senderUser.toLowerCase();

    // ⚡ Case-insensitive local state update
    setMessages(prev => prev.map(m => {
      const mSender = (m.sender_username || m.sender_name || '').toLowerCase();
      if (mSender === targetLower) {
        return { ...m, is_read: true };
      }
      return m;
    }));

    const activeDb = localStorage.getItem('active_company') || 'express_db';
    try {
      await fetch(`/api/chat/read?db=${activeDb}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: currentUser.username,
          senderUsername: senderUser
        })
      });
    } catch (e) {}
  };

  // Start Call (Outgoing Call)
  const handleStartCall = (targetUser: string, type: 'voice' | 'video' = 'voice') => {
    if (!targetUser || targetUser === 'ALL') {
      alert('กรุณาเลือกเพื่อนร่วมงานในทีมที่ต้องการโทรหา');
      return;
    }
    setOutgoingCallModal({ open: true, type, targetUser });
    setIsMuted(false);
    setIsVideoOff(false);

    setupWebRTCPeerConnection(targetUser, true);

    const callMsg = type === 'video' ? `📹 โทรวิดีโอหา @${targetUser} (กำลังรอการรับสาย...)` : `📞 โทรสายด่วนหา @${targetUser} (กำลังรอการรับสาย...)`;
    const activeDb = localStorage.getItem('active_company') || 'express_db';
    fetch(`/api/chat?db=${activeDb}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sender_name: currentUser.name,
        sender_username: currentUser.username,
        receiver_username: targetUser,
        message: callMsg
      })
    }).then(() => fetchMessages(true)).catch(() => {});
  };

  // Accept Incoming Call
  const handleAcceptIncomingCall = () => {
    if (!incomingCall) return;
    const { callerName, callerUsername, type } = incomingCall;

    if (remoteAudioRef.current) {
      remoteAudioRef.current.play().catch(() => {});
    }

    setActiveCallSession({ peerName: callerName, peerUsername: callerUsername, type });
    setIncomingCall(null);
    setIsMuted(false);
    setIsVideoOff(false);

    setupWebRTCPeerConnection(callerUsername, false);

    const activeDb = localStorage.getItem('active_company') || 'express_db';
    fetch(`/api/chat?db=${activeDb}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sender_name: currentUser.name,
        sender_username: currentUser.username,
        receiver_username: callerUsername,
        message: `✅ @${currentUser.username} กดรับสายโทรแล้ว (กำลังสนทนาสาย...)`
      })
    }).then(() => fetchMessages(true)).catch(() => {});
  };

  // Decline Incoming Call
  const handleDeclineIncomingCall = () => {
    if (!incomingCall) return;
    const { callerUsername } = incomingCall;

    setIncomingCall(null);

    const activeDb = localStorage.getItem('active_company') || 'express_db';
    fetch(`/api/chat?db=${activeDb}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sender_name: currentUser.name,
        sender_username: currentUser.username,
        receiver_username: callerUsername,
        message: `❌ @${currentUser.username} ไม่สะดวกรับสายขณะนี้`
      })
    }).then(() => fetchMessages(true)).catch(() => {});
  };

  // End Active Call
  const handleEndActiveCall = () => {
    stopWebRTCCall();
    if (activeCallSession) {
      const activeDb = localStorage.getItem('active_company') || 'express_db';
      const formattedTime = formatTimer(callTimer);
      fetch(`/api/chat?db=${activeDb}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_name: currentUser.name,
          sender_username: currentUser.username,
          receiver_username: activeCallSession.peerUsername,
          message: `🔴 จบการสนทนาสายโทร (ระยะเวลาสนทนา ${formattedTime})`
        })
      }).then(() => fetchMessages(true)).catch(() => {});
    }

    setOutgoingCallModal(null);
    setActiveCallSession(null);
  };

  const formatTimer = (seconds: number) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const fetchAvatars = async () => {
    try {
      const res = await fetch('/api/chat/avatar');
      const json = await res.json();
      if (json.success && json.avatars) {
        setUserAvatars(json.avatars);
      }
    } catch (e) {}
  };

  const fetchUsers = async () => {
    try {
      let apiUsers: any[] = [];
      const res = await fetch('/api/users').catch(() => null);
      if (res && res.ok) {
        const json = await res.json();
        if (json.success && Array.isArray(json.users)) {
          apiUsers = json.users;
        }
      }
      mergeUsersList(apiUsers);
    } catch (e) {}
  };

  const mergeUsersList = (apiUsers: any[] = [], msgList: any[] = []) => {
    setUserList(prev => {
      const userMap = new Map<string, any>();
      DEFAULT_TEAM_USERS.forEach(u => userMap.set(u.username, u));
      prev.forEach(u => u && u.username && userMap.set(u.username, u));
      apiUsers.forEach(u => {
        if (u && u.username) {
          userMap.set(u.username, {
            username: u.username,
            name: u.name || u.description || u.username,
            role: u.role || 'user'
          });
        }
      });
      msgList.forEach(m => {
        if (m && m.sender_username && !userMap.has(m.sender_username)) {
          userMap.set(m.sender_username, {
            username: m.sender_username,
            name: m.sender_name || m.sender_username,
            role: 'user'
          });
        }
      });
      return Array.from(userMap.values());
    });
  };

  const fetchMessages = async (silent = false) => {
    if (!silent) setLoading(true);
    const activeDb = localStorage.getItem('active_company') || 'express_db';
    try {
      const res = await fetch(`/api/chat?db=${activeDb}&limit=100`);
      if (!res.ok) return;
      const json = await res.json();
      if (json.success) {
        const fetchedMsgs: any[] = json.messages || json.data || [];

        if (prevMsgCountRef.current > 0 && fetchedMsgs.length > prevMsgCountRef.current) {
          const latestMsg = fetchedMsgs[fetchedMsgs.length - 1];
          if (latestMsg && (latestMsg.sender_username || latestMsg.sender_name) !== currentUser.username) {
            playNewMessageSound();
          }
        }
        prevMsgCountRef.current = fetchedMsgs.length;

        // Auto mark selected recipient messages as read in local state
        const currentTarget = selectedRecipient.toLowerCase();
        const processedMsgs = fetchedMsgs.map(m => {
          if (currentTarget !== 'all') {
            const mSender = (m.sender_username || m.sender_name || '').toLowerCase();
            if (mSender === currentTarget) {
              return { ...m, is_read: true };
            }
          }
          return m;
        });

        setMessages(processedMsgs);
        mergeUsersList([], fetchedMsgs);

        // 📞 WebRTC Dual-Device Call Signaling & State Sync Engine
        if (currentUser && currentUser.username) {
          const now = Date.now();
          const myUserLower = currentUser.username.toLowerCase();
          const targetTag = `@${myUserLower}`;

          for (let i = fetchedMsgs.length - 1; i >= 0; i--) {
            const m = fetchedMsgs[i];
            const msgText = (m.message || '').toLowerCase();
            const senderUser = (m.sender_username || m.sender_name || '').toLowerCase();
            const receiverUser = (m.receiver_username || '').toLowerCase();
            const createdTime = new Date(m.created_at).getTime();
            const isRecent = (now - createdTime) < 60000;

            // 1. RECEIVER SIDE: Detect Incoming Call signal
            if (
              senderUser !== myUserLower &&
              msgText.includes('(กำลังรอการรับสาย...)') &&
              (msgText.includes(targetTag) || receiverUser === myUserLower)
            ) {
              const callKey = `${m.id}_${createdTime}`;
              if (!handledCallsRef.current.has(callKey) && isRecent && !activeCallSession && !incomingCall) {
                const callType = msgText.includes('วิดีโอ') ? 'video' : 'voice';
                setIncomingCall({
                  callerName: m.sender_name || m.sender_username,
                  callerUsername: m.sender_username || m.sender_name,
                  type: callType,
                  messageId: m.id
                });
                handledCallsRef.current.add(callKey);
                break;
              }
            }

            // 2. CALLER SIDE: Detect Receiver Accepted Call -> Transition Caller to Active Session!
            if (
              outgoingCallModal &&
              senderUser === outgoingCallModal.targetUser.toLowerCase() &&
              (msgText.includes('กดรับสายโทรแล้ว') || msgText.includes('กำลังสนทนาสาย')) &&
              isRecent
            ) {
              const targetName = (userList || []).find(u => u.username.toLowerCase() === outgoingCallModal.targetUser.toLowerCase())?.name || outgoingCallModal.targetUser;
              setActiveCallSession({
                peerName: targetName,
                peerUsername: outgoingCallModal.targetUser,
                type: outgoingCallModal.type
              });
              setOutgoingCallModal(null);
              setAudioConnected(true);
              break;
            }

            // 3. CALLER SIDE: Detect Receiver Declined Call -> Close Calling Modal!
            if (
              outgoingCallModal &&
              senderUser === outgoingCallModal.targetUser.toLowerCase() &&
              (msgText.includes('ไม่สะดวกรับสาย') || msgText.includes('ปฏิเสธสาย')) &&
              isRecent
            ) {
              setOutgoingCallModal(null);
              stopWebRTCCall();
              alert(`@${outgoingCallModal.targetUser} ไม่สะดวกรับสายขณะนี้`);
              break;
            }

            // 4. BOTH SIDES: Detect Ended Call -> Close Active Call Session!
            if (
              activeCallSession &&
              (senderUser === activeCallSession.peerUsername.toLowerCase() || receiverUser === myUserLower) &&
              msgText.includes('จบการสนทนาสายโทร') &&
              isRecent
            ) {
              stopWebRTCCall();
              setActiveCallSession(null);
              setOutgoingCallModal(null);
              break;
            }
          }
        }
      }
    } catch (error) {
      console.error('Fetch Messages Error:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleUploadAvatar = async (file?: File) => {
    if (!file) return;
    setIsUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('username', currentUser.username);

      const res = await fetch('/api/chat/avatar', {
        method: 'POST',
        body: formData
      });
      
      const responseText = await res.text();
      let json: any = {};
      try {
        json = JSON.parse(responseText);
      } catch (parseErr) {
        alert('เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ กรุณาลองใหม่อีกครั้ง');
        setIsUploadingAvatar(false);
        return;
      }

      if (json.success && json.avatarUrl) {
        setUserAvatars(prev => ({ ...prev, [currentUser.username]: json.avatarUrl }));
        alert('อัปโหลดรูปโปรไฟล์สำเร็จ! 📷✨');
      } else {
        alert(json.error || 'เกิดข้อผิดพลาดในการอัปโหลดรูปโปรไฟล์');
      }
    } catch (e: any) {
      alert('เกิดข้อผิดพลาด: ' + e.message);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleInputFocus = () => {
    setTimeout(() => {
      if (typeof window !== 'undefined') {
        window.scrollTo(0, 0);
      }
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 150);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setTextInput(val);

    const cursorPos = e.target.selectionStart || val.length;
    const textBeforeCursor = val.slice(0, cursorPos);

    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const charAfterAt = textBeforeCursor.slice(lastAtIndex);
      if (!charAfterAt.includes(' ')) {
        setShowMentionMenu(true);
        setMentionSearch(charAfterAt.slice(1));
        return;
      }
    }
    setShowMentionMenu(false);
  };

  const handleSelectMention = (user: any) => {
    if (!inputRef.current) return;
    const cursorPos = inputRef.current.selectionStart || textInput.length;
    const textBeforeCursor = textInput.slice(0, cursorPos);
    const textAfterCursor = textInput.slice(cursorPos);

    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const newText = textBeforeCursor.slice(0, lastAtIndex) + `@${user.username} ` + textAfterCursor;
      setTextInput(newText);
    } else {
      setTextInput(textInput + `@${user.username} `);
    }
    setShowMentionMenu(false);
    inputRef.current.focus();
  };

  const triggerAtSymbol = () => {
    setTextInput(prev => prev + '@');
    setShowMentionMenu(true);
    setMentionSearch('');
    if (inputRef.current) inputRef.current.focus();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setPreviewUrl('');
  };

  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!textInput.trim() && !selectedFile) return;

    setIsUploading(true);
    setShowMentionMenu(false);
    const activeDb = localStorage.getItem('active_company') || 'express_db';

    try {
      let imageUrl = '';

      if (selectedFile) {
        const formData = new FormData();
        formData.append('file', selectedFile);
        const upRes = await fetch('/api/chat/upload', {
          method: 'POST',
          body: formData
        });
        const upJson = await upRes.json();
        if (upJson.success) {
          imageUrl = upJson.imageUrl;
        } else {
          alert('เกิดข้อผิดพลาดในการอัปโหลดรูปภาพ: ' + upJson.error);
          setIsUploading(false);
          return;
        }
      }

      let sendMsg = textInput.trim();
      let targetRecipient = 'ALL';

      if (selectedRecipient && selectedRecipient !== 'ALL') {
        targetRecipient = selectedRecipient;
        const recUser = (userList || []).find(u => u.username === selectedRecipient);
        const tag1 = `@${selectedRecipient}`;
        const tag2 = recUser ? `@${recUser.name}` : '';
        if (!sendMsg.includes(tag1) && (tag2 ? !sendMsg.includes(tag2) : true)) {
          sendMsg = `${tag1} ${sendMsg}`.trim();
        }
      }

      const postRes = await fetch(`/api/chat?db=${activeDb}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_name: currentUser.name,
          sender_username: currentUser.username,
          receiver_username: targetRecipient,
          message: sendMsg,
          image_url: imageUrl
        })
      });

      const postJson = await postRes.json();
      if (postJson.success) {
        setTextInput('');
        handleClearFile();
        fetchMessages(true);
      } else {
        alert(postJson.error || 'เกิดข้อผิดพลาดในการส่งข้อความ');
      }
    } catch (e: any) {
      alert('เกิดข้อผิดพลาด: ' + e.message);
    } finally {
      setIsUploading(false);
    }
  };

  const renderFormattedMessage = (msgText: string) => {
    if (!msgText) return null;
    const parts = msgText.split(/(@[\wก-๙]+)/g);
    return parts.map((part, index) => {
      if (part.startsWith('@')) {
        return (
          <span 
            key={index} 
            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 mx-0.5 bg-purple-500/30 text-purple-200 border border-purple-400/40 rounded font-bold text-xs shadow-sm"
          >
            <AtSign size={11} className="text-purple-300" />
            {part.substring(1)}
          </span>
        );
      }
      return part;
    });
  };

  const filteredUsers = (userList || []).filter(u => {
    if (!u) return false;
    const uName = (u.name || '').toLowerCase();
    const uUser = (u.username || '').toLowerCase();
    const search = (mentionSearch || '').toLowerCase();
    return uUser.includes(search) || uName.includes(search);
  });

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-2 sm:p-4 md:p-6 flex flex-col justify-between max-w-[1600px] mx-auto w-full">
      
      {/* 🔊 HIDDEN REMOTE AUDIO PLAYER */}
      <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" />

      {/* 🚨 KPI & SLA RESPONSE TIME WARNING BANNER */}
      <div className="mb-3 bg-gradient-to-r from-rose-950/90 via-slate-900 to-purple-950/90 border border-rose-500/40 rounded-2xl px-3.5 py-2.5 flex flex-wrap items-center justify-between gap-2 shadow-lg">
        <div className="flex items-center gap-2.5 text-xs">
          <span className="px-2 py-1 rounded-lg bg-rose-600 text-white font-extrabold text-[11px] flex items-center gap-1 shadow animate-pulse shrink-0">
            ⏱️ กฎ KPI การตอบแชท
          </span>
          <div className="text-slate-200 font-medium leading-snug">
            <span className="text-emerald-400 font-bold">⚡ ตอบภายใน 5 นาที = KPI 100% (ดีเยี่ยม)</span>
            <span className="mx-1.5 text-slate-600">|</span>
            <span className="text-amber-300 font-bold">⏳ 5-15 นาที = ปกติ (80%)</span>
            <span className="mx-1.5 text-slate-600">|</span>
            <span className="text-rose-400 font-extrabold underline decoration-rose-500/60">🚨 ตอบช้าเกิน 15 นาที หรือดองแชท = หักคะแนน KPI ประจำเดือน!</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setShowKpiModal(true)}
          className="px-3 py-1.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs rounded-xl shadow-md transition flex items-center gap-1.5 shrink-0 cursor-pointer"
        >
          <span>📊 ดูคะแนน KPI การตอบแชท</span>
        </button>
      </div>

      {/* 💬 CLEAN 2-COLUMN GRID LAYOUT */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4 flex-1 w-full">

        {/* 👥 LEFT VERTICAL USER SIDEBAR */}
        <div className={`border border-slate-800 rounded-2xl p-3 sm:p-4 shadow-xl flex-col overflow-hidden w-full md:col-span-1 md:flex md:static md:z-auto md:bg-slate-900 md:p-4 md:h-[86vh] ${
          mobileTab === 'users' 
            ? 'flex fixed inset-0 z-40 bg-slate-950 p-4 h-full w-full' 
            : 'hidden'
        }`}>
          
          {/* Current Logged In User Profile Card & Upload Button in Left Sidebar */}
          <div className="bg-slate-950/90 border border-slate-800 p-2.5 rounded-2xl mb-3 flex items-center justify-between shadow-inner shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-xl bg-purple-950 border border-purple-600 overflow-hidden relative flex items-center justify-center text-purple-200 font-extrabold text-xs shrink-0 shadow-md">
                {userAvatars[currentUser.username] ? (
                  <img src={userAvatars[currentUser.username]} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  (currentUser.username || currentUser.name || 'U').charAt(0).toUpperCase()
                )}
                <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-slate-950"></span>
              </div>
              <div className="min-w-0">
                <div className="text-white font-bold text-xs truncate max-w-[90px] sm:max-w-[110px] flex items-center gap-1">
                  {currentUser.name}
                </div>
                <div className="text-[10px] text-emerald-400 font-mono truncate font-bold flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span> ออนไลน์
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1 shrink-0">
              <label className="px-2 py-1 bg-purple-950/90 hover:bg-purple-900 border border-purple-700/60 text-purple-200 rounded-xl text-[10px] font-bold cursor-pointer transition flex items-center gap-1 shadow-sm">
                <Camera size={11} className="text-purple-300" />
                {isUploadingAvatar ? '...' : 'รูปโปรไฟล์'}
                <input 
                  type="file" 
                  accept="image/*" 
                  className="hidden"
                  onChange={(e) => handleUploadAvatar(e.target.files?.[0])}
                />
              </label>

              <button 
                onClick={() => fetchMessages()} 
                className="p-1 text-slate-400 hover:text-white transition rounded-lg hover:bg-slate-800 shrink-0"
                title="รีเฟรชข้อความ"
              >
                <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-800 shrink-0">
            <span className="text-xs sm:text-sm font-extrabold text-white flex items-center gap-1.5">
              <Users size={15} className="text-purple-400" /> รายชื่อผู้สนทนาในทีม
            </span>
            <span className="text-[10px] text-purple-300 bg-purple-950/80 px-2 py-0.5 rounded-full border border-purple-800 font-bold">
              {(userList || []).length} คน
            </span>
          </div>

          {/* Search User Input */}
          <div className="mb-2.5 relative shrink-0">
            <Search size={13} className="absolute left-2.5 top-2.5 text-slate-500" />
            <input
              type="text"
              placeholder="ค้นหาชื่อเพื่อน..."
              value={searchUserQuery}
              onChange={(e) => setSearchUserQuery(e.target.value)}
              className="w-full bg-slate-950 border border-slate-800 rounded-xl pl-7 pr-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 font-medium"
            />
          </div>

          {/* Vertical User List Container */}
          <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 divide-y divide-slate-800/40 no-scrollbar">
            
            {/* Public Group Chat Channel */}
            <button
              type="button"
              onClick={() => handleSelectUserChannel('ALL')}
              className={`w-full p-2.5 rounded-2xl text-xs font-bold transition flex items-center justify-between text-left ${
                selectedRecipient === 'ALL'
                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-950/60 border border-purple-400/50 ring-2 ring-purple-400/50'
                  : 'bg-slate-950/90 hover:bg-slate-800 text-slate-300 border border-slate-800'
              }`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8.5 h-8.5 rounded-xl bg-purple-500/20 text-purple-300 flex items-center justify-center font-bold text-sm shrink-0 border border-purple-500/30">
                  🌐
                </div>
                <div className="truncate">
                  <div className="font-extrabold text-xs sm:text-sm truncate">ทุกคนในทีม (Public)</div>
                  <div className="text-[9px] text-slate-400 font-normal truncate">แชทรวมทั้งบริษัท</div>
                </div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-md shrink-0 ml-1 ${selectedRecipient === 'ALL' ? 'bg-white text-purple-950 font-extrabold' : 'bg-slate-800 text-purple-300 border border-slate-700'}`}>
                {messages.length}
              </span>
            </button>

            {/* Individual Vertical Users with Avatar, ONLINE STATUS DOT, & UNREAD BADGES */}
            {(userList || []).filter(u => u && u.username !== 'ทุกคน' && u.username !== currentUser.username && (
              !searchUserQuery ||
              (u.name || '').toLowerCase().includes(searchUserQuery.toLowerCase()) ||
              (u.username || '').toLowerCase().includes(searchUserQuery.toLowerCase())
            )).map((u, idx) => {
              const isSelected = selectedRecipient.toLowerCase() === u.username.toLowerCase();
              const isOnline = onlineUsers.some(o => o.toLowerCase() === u.username.toLowerCase()) || u.username === 'admin' || u.username === 'navy';
              
              const userMsgCount = messages.filter(m => {
                const mSender = (m.sender_username || m.sender_name || '').toLowerCase();
                const uUser = u.username.toLowerCase();
                const uName = (u.name || '').toLowerCase();
                return mSender === uUser || mSender === uName;
              }).length;

              // ⚡ Case-insensitive Unread messages count, GUARANTEED 0 if selected!
              const unreadCount = isSelected ? 0 : messages.filter(m => {
                if (m.is_read === true) return false;
                const mSender = (m.sender_username || m.sender_name || '').toLowerCase();
                const uUser = u.username.toLowerCase();
                const uName = (u.name || '').toLowerCase();
                const mReceiver = (m.receiver_username || '').toLowerCase();
                const myUser = (currentUser.username || '').toLowerCase();
                
                const isFromU = (mSender === uUser || mSender === uName);
                const isToMe = (mReceiver === myUser || mReceiver === 'all' || !mReceiver);
                return isFromU && isToMe;
              }).length;

              const avatar = userAvatars[u.username];

              return (
                <button
                  key={u.username || idx}
                  type="button"
                  onClick={() => handleSelectUserChannel(u.username)}
                  className={`w-full p-2.5 rounded-2xl text-xs font-bold transition flex items-center justify-between text-left mt-1.5 ${
                    isSelected
                      ? 'bg-purple-600 text-white shadow-lg shadow-purple-950/60 border border-purple-400/50 ring-2 ring-purple-400/50'
                      : unreadCount > 0 
                        ? 'bg-purple-950/40 hover:bg-purple-900/60 text-white border-2 border-rose-500/80 shadow-md'
                        : 'bg-slate-950/90 hover:bg-slate-800 text-slate-300 border border-slate-800/80'
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className={`w-8.5 h-8.5 rounded-xl font-extrabold text-xs flex items-center justify-center shrink-0 border overflow-hidden relative ${
                      isSelected
                        ? 'bg-white text-purple-900 border-white'
                        : 'bg-purple-950/60 text-purple-300 border-purple-700/60'
                    }`}>
                      {avatar ? (
                        <img src={avatar} alt={u.name} className="w-full h-full object-cover" />
                      ) : (
                        (u.username || u.name || 'U').charAt(0).toUpperCase()
                      )}
                      
                      <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-slate-950 ${
                        isOnline ? 'bg-emerald-500 animate-pulse' : 'bg-slate-500'
                      }`}></span>
                    </div>

                    <div className="truncate">
                      <div className="font-bold text-xs sm:text-sm truncate flex items-center gap-1">
                        {u.name || u.username}
                      </div>
                      <div className="text-[10px] font-mono font-normal truncate flex items-center gap-1">
                        <span className={isOnline ? 'text-emerald-400 font-bold' : 'text-slate-500'}>
                          {isOnline ? '🟢 ออนไลน์' : '⚪ ออฟไลน์'}
                        </span>
                        <span className="text-slate-500">• @{u.username}</span>
                      </div>
                      {(() => {
                        const kpi = getUserKpiStats(u.username);
                        return (
                          <div className="flex items-center gap-1 mt-1 flex-wrap">
                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-extrabold border ${
                              kpi.kpiScore >= 90
                                ? 'bg-emerald-950/80 text-emerald-300 border-emerald-500/40'
                                : kpi.kpiScore >= 75
                                ? 'bg-amber-950/80 text-amber-300 border-amber-500/40'
                                : 'bg-rose-950/90 text-rose-300 border-rose-500/60 animate-pulse'
                            }`}>
                              KPI {kpi.kpiScore}% ({kpi.grade})
                            </span>
                            {kpi.repliedCount > 0 && (
                              <span className="text-[9px] text-slate-400">
                                เฉลี่ย {formatDurationShort(kpi.avgSec)}
                              </span>
                            )}
                            {kpi.pendingCount > 0 && (
                              <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
                                kpi.maxPendingSeconds > 900
                                  ? 'bg-rose-600 text-white animate-bounce'
                                  : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                              }`}>
                                {kpi.maxPendingSeconds > 900 ? `🚨 ดองแชท ${formatDurationShort(kpi.maxPendingSeconds)} (หัก KPI!)` : `⏳ รอตอบ ${formatDurationShort(kpi.maxPendingSeconds)}`}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0 ml-1">
                    {/* 🔴 UNREAD MESSAGE COUNTER BADGE (ONLY SHOWS WHEN THERE ARE UNREAD MESSAGES!) */}
                    {unreadCount > 0 && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-black bg-rose-600 text-white shadow-lg shadow-rose-950/80 animate-bounce border border-rose-400 flex items-center gap-0.5 shrink-0">
                        <Bell size={10} className="animate-spin" />
                        {unreadCount} ใหม่
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleStartCall(u.username, 'voice'); }}
                      className="p-1.5 text-emerald-400 hover:text-white hover:bg-emerald-600/80 rounded-lg transition"
                      title={`โทรสายด่วนหา @${u.username}`}
                    >
                      <Phone size={13} />
                    </button>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* 💬 RIGHT CHAT STREAM CONTAINER (Col 2-4 on Desktop) */}
        <div 
          style={mobileTab === 'chat' && viewportHeight ? { height: `${viewportHeight}px` } : undefined}
          className={`border border-slate-800 rounded-2xl flex-col overflow-hidden shadow-2xl relative w-full md:col-span-3 md:flex md:static md:z-auto md:bg-slate-900 md:h-[86vh] ${
            mobileTab === 'chat' 
              ? 'flex fixed inset-0 z-50 bg-slate-950 h-full w-full' 
              : 'hidden'
          }`}
        >
          
          {/* Active Conversation Top Banner (Compact 1-Line Header) */}
          <div className="px-3 py-2.5 md:px-4 md:py-3 bg-slate-950 border-b border-slate-800 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {/* Prominent Mobile Back Button to User Directory */}
              <button
                type="button"
                onClick={() => setMobileTab('users')}
                className="md:hidden px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white font-extrabold rounded-xl text-xs transition flex items-center gap-1 shrink-0 shadow-lg shadow-purple-950/60 border border-purple-400/50 active:scale-95"
                title="กลับไปหน้ารายชื่อเพื่อน"
              >
                <ChevronLeft size={18} className="text-purple-200" />
                <span>รายชื่อเพื่อน</span>
              </button>
              
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0"></span>
              
              <div className="flex items-center gap-1.5 min-w-0">
                {selectedRecipient === 'ALL' ? (
                  <span className="text-xs md:text-sm font-extrabold text-white truncate">🌐 แชทรวมทีม (Public)</span>
                ) : (
                  <>
                    <div className="w-6 h-6 rounded-lg bg-purple-950 border border-purple-500 overflow-hidden flex items-center justify-center text-purple-200 text-[10px] font-bold shrink-0">
                      {userAvatars[selectedRecipient] ? (
                        <img src={userAvatars[selectedRecipient]} alt={selectedRecipient} className="w-full h-full object-cover" />
                      ) : (
                        selectedRecipient.charAt(0).toUpperCase()
                      )}
                    </div>
                    <span className="text-xs md:text-sm font-extrabold text-white truncate">@{selectedRecipient}</span>
                  </>
                )}
              </div>
            </div>

            {selectedRecipient !== 'ALL' && (
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  onClick={() => handleStartCall(selectedRecipient, 'voice')}
                  className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-extrabold shadow-lg transition flex items-center gap-1"
                  title="โทรสายด่วนคุยเสียง"
                >
                  <Phone size={13} /> <span className="hidden sm:inline">โทรเสียง</span>
                </button>
                <button
                  onClick={() => handleStartCall(selectedRecipient, 'video')}
                  className="px-2.5 py-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-extrabold shadow-lg transition flex items-center gap-1"
                  title="โทรวิดีโอคอล"
                >
                  <Video size={13} /> <span className="hidden sm:inline">โทรวิดีโอ</span>
                </button>
                <button 
                  onClick={() => setSelectedRecipient('ALL')}
                  className="px-2 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 rounded-xl text-[11px] font-bold transition flex items-center gap-0.5"
                >
                  ↩ <span className="hidden sm:inline">สลับกลับแชทรวม</span>
                </button>
              </div>
            )}
          </div>

          {/* Chat Stream Messages List (Smart Scrolling & Touch Pan Enabled) */}
          <div 
            ref={chatContainerRef}
            onScroll={handleChatScroll}
            className="flex-1 p-3 sm:p-4 md:p-6 overflow-y-auto overscroll-contain space-y-3 bg-slate-950/50 min-h-0 relative touch-pan-y"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {loading && messages.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-500 text-xs">
                <RefreshCw className="animate-spin mr-2" size={16} /> กำลังโหลดข้อความแชท...
              </div>
            ) : messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-500 text-xs space-y-2 py-12">
                <MessageSquare size={48} className="opacity-20 text-purple-400" />
                <p>ยังไม่มีข้อความแชท พิมพ์ <span className="text-purple-400 font-bold">@</span> เพื่อแท็กรายชื่อเพื่อนร่วมงาน!</p>
              </div>
            ) : (
              messages.filter(msg => {
                const myUser = (currentUser.username || currentUser.name || '').toLowerCase();
                const targetUser = (selectedRecipient || 'ALL').toLowerCase();

                const msgSender = (msg.sender_username || msg.sender_name || '').toLowerCase();
                const msgReceiver = (msg.receiver_username || '').toLowerCase();
                const msgText = (msg.message || '').toLowerCase();

                // 🌐 Public Team Channel: Show public messages or general broadcasts
                if (selectedRecipient === 'ALL') {
                  return !msgReceiver || msgReceiver === 'all';
                }

                // 🔒 Private 1-on-1 Chat Channel: Show ONLY private messages between ME & Selected User
                const isMeToTarget = (msgSender === myUser) && (msgReceiver === targetUser || msgText.includes('@' + targetUser));
                const isTargetToMe = (msgSender === targetUser) && (msgReceiver === myUser || msgText.includes('@' + myUser) || msgReceiver === targetUser);

                return isMeToTarget || isTargetToMe;
              }).map((msg, idx) => {
                const isMe = msg.sender_name === currentUser.name || msg.sender_username === currentUser.username;
                const senderUsername = msg.sender_username || msg.sender_name;
                const senderAvatar = userAvatars[senderUsername];

                return (
                  <div 
                    key={msg.id || idx}
                    className={`flex flex-col ${isMe ? 'items-end' : 'items-start'} group animate-in fade-in duration-200`}
                  >
                    <div className="flex items-center gap-1.5 mb-1 px-1">
                      {!isMe && (
                        <div className="w-5 h-5 rounded-full bg-purple-950 border border-purple-700/80 overflow-hidden flex items-center justify-center text-purple-300 text-[9px] font-bold shrink-0">
                          {senderAvatar ? (
                            <img src={senderAvatar} alt={msg.sender_name} className="w-full h-full object-cover" />
                          ) : (
                            (senderUsername || 'U').charAt(0).toUpperCase()
                          )}
                        </div>
                      )}
                      <span className="text-[11px] font-bold text-slate-300">
                        {isMe ? 'คุณ (' + msg.sender_name + ')' : msg.sender_name}
                      </span>
                      <span className="text-[10px] text-slate-500">
                        • {new Date(msg.created_at).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })} น.
                      </span>
                    </div>

                    <div className={`max-w-[88%] md:max-w-[70%] p-3 rounded-2xl shadow-lg transition relative ${
                      isMe 
                        ? 'bg-purple-600 text-white rounded-tr-none border border-purple-400/40' 
                        : 'bg-slate-900 text-slate-100 rounded-tl-none border border-slate-800'
                    }`}>
                      {msg.image_url && (
                        <div className="mb-2 overflow-hidden rounded-xl border border-black/20 max-w-[240px] sm:max-w-sm cursor-pointer group relative">
                          <img 
                            src={msg.image_url} 
                            alt="แนบรูปภาพ" 
                            className="w-full h-auto max-h-60 object-cover transition group-hover:scale-105"
                            onClick={() => setLightboxImage(msg.image_url)}
                          />
                          <span className="absolute bottom-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full backdrop-blur">
                            คลิกขยายใหญ่ 🔍
                          </span>
                        </div>
                      )}

                      {msg.message && (
                        <p className="text-xs md:text-sm leading-relaxed whitespace-pre-wrap break-words font-medium">
                          {renderFormattedMessage(msg.message)}
                        </p>
                      )}

                      {/* ⏱️ SLA & KPI RESPONSE TIME TRACKER ON EACH MESSAGE */}
                      {(() => {
                        const targetUser = getMessageTargetUser(msg);
                        if (!targetUser) return null;
                        const msgTime = new Date(msg.created_at).getTime();
                        let respSec = msg.response_seconds ? Number(msg.response_seconds) : null;
                        if (respSec === null) {
                          const replyMsg = messages.find(r => {
                            const rSender = (r.sender_username || r.sender_name || '').toLowerCase();
                            return rSender === targetUser.toLowerCase() && new Date(r.created_at).getTime() > msgTime;
                          });
                          if (replyMsg) {
                            respSec = Math.max(1, Math.floor((new Date(replyMsg.created_at).getTime() - msgTime) / 1000));
                          }
                        }

                        if (respSec !== null) {
                          const isFast = respSec <= 300;
                          const isNormal = respSec > 300 && respSec <= 900;
                          return (
                            <div className="mt-1.5 pt-1 border-t border-white/10 flex items-center justify-between gap-2 text-[10px]">
                              <span className={`px-1.5 py-0.5 rounded font-extrabold flex items-center gap-1 ${
                                isFast
                                  ? 'bg-emerald-950/80 text-emerald-300 border border-emerald-400/40'
                                  : isNormal
                                  ? 'bg-amber-950/80 text-amber-300 border border-amber-400/40'
                                  : 'bg-rose-950/90 text-rose-200 border border-rose-400/60'
                              }`}>
                                {isFast
                                  ? `⚡ @${targetUser} ตอบไวใน ${formatDurationShort(respSec)} (+KPI ดีเยี่ยม)`
                                  : isNormal
                                  ? `⏱️ @${targetUser} ตอบใน ${formatDurationShort(respSec)} (KPI ปกติ)`
                                  : `🚨 @${targetUser} ตอบช้า ${formatDurationShort(respSec)} (เกิน 15 นาที - หัก KPI!)`}
                              </span>
                            </div>
                          );
                        }

                        const waitSec = msg.waiting_seconds !== undefined
                          ? Number(msg.waiting_seconds)
                          : Math.max(0, Math.floor((nowTick - msgTime) / 1000));
                        const isOver15 = waitSec > 900;
                        const isOver5 = waitSec > 300;

                        return (
                          <div className="mt-1.5 pt-1 border-t border-white/10 flex items-center justify-between gap-2 text-[10px]">
                            <span className={`px-2 py-0.5 rounded font-extrabold flex items-center gap-1 ${
                              isOver15
                                ? 'bg-rose-600 text-white border border-rose-300 shadow-lg animate-pulse'
                                : isOver5
                                ? 'bg-amber-500/20 text-amber-200 border border-amber-400/50'
                                : 'bg-slate-800/80 text-slate-300 border border-slate-600'
                            }`}>
                              {isOver15
                                ? `🚨 @${targetUser} ยังไม่ตอบ นาน ${formatDurationShort(waitSec)}! (เกิน 15 นาที มีผลหัก KPI!)`
                                : isOver5
                                ? `⚠️ รอ @${targetUser} ตอบ ${formatDurationShort(waitSec)} (เกิน 5 นาทีแล้ว รีบตอบก่อนถูกหัก KPI!)`
                                : `⏳ รอ @${targetUser} ตอบกลับ (${formatDurationShort(waitSec)})`}
                            </span>
                          </div>
                        );
                      })()}

                      {/* 👁️ MESSAGE READ STATUS / DELIVERY RECEIPTS */}
                      {isMe && (
                        <div className="flex items-center justify-end gap-1 mt-1 pt-0.5 text-[10px]">
                          {msg.is_read ? (
                            <span className="text-sky-200 font-extrabold flex items-center gap-0.5 bg-sky-950/60 px-1.5 py-0.5 rounded border border-sky-400/50 shadow-sm">
                              <CheckCheck size={12} className="text-sky-300" /> อ่านแล้ว
                            </span>
                          ) : (
                            <span className="text-purple-200/80 font-medium flex items-center gap-0.5 bg-purple-950/40 px-1.5 py-0.5 rounded border border-purple-400/30">
                              <Check size={11} className="text-purple-300" /> ส่งแล้ว
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />

            {/* ⬇️ FLOATING SCROLL TO LATEST MESSAGES BUTTON */}
            {isUserScrolledUp && (
              <button
                type="button"
                onClick={() => {
                  setIsUserScrolledUp(false);
                  messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="sticky bottom-3 left-1/2 -translate-x-1/2 z-30 px-4 py-2 bg-purple-600/90 hover:bg-purple-500 text-white font-extrabold text-xs rounded-full shadow-2xl backdrop-blur-md border border-purple-400/50 flex items-center gap-1.5 animate-bounce transition active:scale-95"
              >
                <span>⬇️ เลื่อนลงไปข้อความล่าสุด</span>
              </button>
            )}
          </div>

          {/* Selected Image Preview */}
          {previewUrl && (
            <div className="px-3 py-2 bg-slate-950 border-t border-slate-800 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <img src={previewUrl} alt="Preview" className="w-10 h-10 object-cover rounded-lg border border-purple-500/50" />
                <div>
                  <span className="text-xs text-purple-300 font-bold block">📷 แนบรูปภาพแล้ว</span>
                  <span className="text-[10px] text-slate-500 block truncate max-w-[150px] sm:max-w-xs">{selectedFile?.name}</span>
                </div>
              </div>
              <button 
                onClick={handleClearFile} 
                className="p-1 text-rose-400 hover:text-rose-300 hover:bg-rose-950/50 rounded-lg transition"
              >
                <X size={18} />
              </button>
            </div>
          )}

          {/* 🏷️ MENTION DROPDOWN POPPER */}
          <div className="relative">
            {showMentionMenu && (
              <div className="absolute bottom-2 left-3 z-50 w-64 sm:w-72 bg-slate-900 border border-purple-500/50 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-md animate-in fade-in slide-in-from-bottom-2">
                <div className="px-3 py-2 bg-purple-950/60 border-b border-purple-500/30 flex items-center justify-between text-xs font-bold text-purple-300">
                  <span className="flex items-center gap-1.5"><AtSign size={14} /> เลือกเพื่อนร่วมงานที่ต้องการแท็ก</span>
                  <button onClick={() => setShowMentionMenu(false)} className="text-slate-400 hover:text-white"><X size={14} /></button>
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-slate-800/60">
                  {filteredUsers.length === 0 ? (
                    <div className="p-3 text-center text-xs text-slate-500">ไม่พบรายชื่อผู้ใช้ "{mentionSearch}"</div>
                  ) : (
                    filteredUsers.map((user, idx) => (
                      <div 
                        key={idx}
                        onClick={() => handleSelectMention(user)}
                        className="px-3 py-2.5 hover:bg-purple-600/30 cursor-pointer flex items-center justify-between transition group"
                      >
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-purple-600/30 border border-purple-400/40 flex items-center justify-center text-purple-300 text-xs font-bold shrink-0 overflow-hidden">
                            {userAvatars[user.username] ? (
                              <img src={userAvatars[user.username]} alt={user.name} className="w-full h-full object-cover" />
                            ) : (
                              (user.username || user.name || 'U').charAt(0).toUpperCase()
                            )}
                          </div>
                          <div>
                            <div className="text-xs font-bold text-white group-hover:text-purple-300">{user.name}</div>
                            <div className="text-[10px] text-slate-400 font-mono">@{user.username || user.name}</div>
                          </div>
                        </div>
                        <span className="text-[10px] px-2 py-0.5 rounded-md bg-slate-800 text-slate-400 border border-slate-700">
                          {user.role}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Chat Input Footer Form */}
          <form onSubmit={handleSendMessage} className="sticky bottom-0 left-0 right-0 z-30 p-2 sm:p-3 bg-slate-900 border-t border-slate-800 flex items-center gap-1.5 shrink-0 pb-[max(0.75rem,env(safe-area-inset-bottom))] shadow-2xl w-full">
            <label className="p-2 sm:p-2.5 bg-slate-950 hover:bg-slate-800 border border-slate-700 text-slate-300 hover:text-white rounded-xl cursor-pointer transition shrink-0 flex items-center gap-1 text-xs font-bold shadow-sm">
              <ImageIcon size={18} className="text-purple-400" />
              <span className="hidden sm:inline">แนบรูป</span>
              <input 
                type="file" 
                accept="image/*" 
                onChange={handleFileSelect} 
                className="hidden" 
              />
            </label>

            <input
              ref={inputRef}
              type="text"
              value={textInput}
              onFocus={handleInputFocus}
              onChange={handleInputChange}
              placeholder="พิมพ์ข้อความ... (Enter เพื่อส่ง)"
              className="flex-1 bg-slate-950 border border-slate-700 rounded-xl px-3 sm:px-4 py-2 text-white text-base sm:text-xs md:text-sm font-medium focus:outline-none focus:border-purple-500 shadow-inner min-w-0"
            />

            <button
              type="submit"
              disabled={isUploading || (!textInput.trim() && !selectedFile)}
              className="px-3.5 sm:px-5 py-2 sm:py-2.5 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white font-bold rounded-xl text-xs md:text-sm transition shadow-lg shadow-purple-950/50 flex items-center gap-1.5 shrink-0"
            >
              {isUploading ? (
                <RefreshCw size={16} className="animate-spin" />
              ) : (
                <>
                  <Send size={15} />
                  <span className="hidden sm:inline">ส่งข้อความ</span>
                </>
              )}
            </button>
          </form>

        </div>
      </div>

      {/* 🔔 INCOMING CALL POP-UP ALERT MODAL (WITH RINGTONE AUDIO) */}
      {incomingCall && (
        <div className="fixed inset-0 bg-black/85 z-50 flex items-center justify-center p-4 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-slate-900 border-2 border-emerald-500/80 rounded-3xl p-6 sm:p-8 max-w-md w-full text-center shadow-2xl flex flex-col items-center relative overflow-hidden animate-pulse">
            <div className="absolute inset-0 bg-gradient-to-b from-emerald-600/20 to-transparent pointer-events-none"></div>
            
            <div className="flex items-center justify-center gap-2 mb-6">
              <span className="bg-emerald-950 text-emerald-300 border border-emerald-600 px-4 py-1.5 rounded-full text-xs font-black animate-bounce flex items-center gap-1.5 shadow-lg">
                <PhoneIncoming size={16} className="animate-spin" />
                {incomingCall.type === 'video' ? '📹 สายเรียกเข้าวิดีโอคอล!' : '📞 มีสายด่วนโทรเข้ามา!'}
              </span>
              <span className="bg-purple-950 text-purple-300 border border-purple-700 px-2.5 py-1 rounded-full text-[11px] font-bold flex items-center gap-1">
                <Volume2 size={13} className="animate-ping" /> เสียงกระดิ่งดัง
              </span>
            </div>

            <div className="relative mb-6">
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl bg-purple-950 border-4 border-emerald-400 overflow-hidden flex items-center justify-center text-white font-black text-2xl sm:text-3xl shadow-2xl relative z-10">
                {userAvatars[incomingCall.callerUsername] ? (
                  <img src={userAvatars[incomingCall.callerUsername]} alt={incomingCall.callerName} className="w-full h-full object-cover" />
                ) : (
                  (incomingCall.callerUsername || 'U').charAt(0).toUpperCase()
                )}
              </div>
              <div className="absolute inset-0 rounded-3xl bg-emerald-500/40 animate-ping"></div>
            </div>

            <h3 className="text-xl sm:text-2xl font-black text-white mb-1">
              {incomingCall.callerName}
            </h3>
            <p className="text-purple-300 font-mono text-xs font-bold mb-6">@{incomingCall.callerUsername}</p>

            <p className="text-slate-300 text-xs mb-6 font-medium">
              กำลังส่งสัญญาณเสียงเรียกเข้าถึงคุณ... เลือกกดรับสายเพื่อเริ่มสนทนา
            </p>

            <div className="flex items-center justify-center gap-4 w-full z-10">
              <button
                type="button"
                onClick={handleDeclineIncomingCall}
                className="flex-1 py-3 px-4 bg-rose-600 hover:bg-rose-500 text-white font-extrabold rounded-2xl border border-rose-500 transition shadow-xl flex items-center justify-center gap-1.5 text-xs sm:text-sm"
              >
                <PhoneOff size={18} /> ปฏิเสธสาย
              </button>

              <button
                type="button"
                onClick={handleAcceptIncomingCall}
                className="flex-1 py-3 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-extrabold rounded-2xl border border-emerald-400 transition shadow-xl shadow-emerald-950/80 transform hover:scale-105 flex items-center justify-center gap-1.5 text-xs sm:text-sm animate-pulse"
              >
                <Phone size={18} /> กดรับสาย
              </button>
            </div>

          </div>
        </div>
      )}

      {/* 📞 OUTGOING CALL MODAL */}
      {outgoingCallModal && outgoingCallModal.open && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-purple-500/50 rounded-3xl p-6 sm:p-8 max-w-md w-full text-center shadow-2xl flex flex-col items-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-purple-600/20 to-transparent pointer-events-none"></div>
            
            <span className="bg-purple-950/80 text-purple-300 border border-purple-700/60 px-3 py-1 rounded-full text-xs font-extrabold mb-6 animate-pulse">
              {outgoingCallModal.type === 'video' ? '📹 กำลังโทรออกวิดีโอคอล...' : '📞 กำลังโทรออกสายด่วน...'}
            </span>

            <div className="relative mb-6">
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-3xl bg-purple-950 border-4 border-purple-500/80 overflow-hidden flex items-center justify-center text-white font-black text-2xl sm:text-3xl shadow-2xl relative z-10">
                {userAvatars[outgoingCallModal.targetUser] ? (
                  <img src={userAvatars[outgoingCallModal.targetUser]} alt={outgoingCallModal.targetUser} className="w-full h-full object-cover" />
                ) : (
                  outgoingCallModal.targetUser.charAt(0).toUpperCase()
                )}
              </div>
              <div className="absolute inset-0 rounded-3xl bg-purple-500/30 animate-ping"></div>
            </div>

            <h3 className="text-xl sm:text-2xl font-black text-white mb-1">
              {(userList || []).find(u => u.username === outgoingCallModal.targetUser)?.name || outgoingCallModal.targetUser}
            </h3>
            <p className="text-purple-300 font-mono text-xs font-bold mb-6">@{outgoingCallModal.targetUser}</p>

            <div className="flex items-center justify-center gap-2 mb-8 text-xs font-semibold text-emerald-400 bg-emerald-950/40 border border-emerald-800/60 px-4 py-2 rounded-xl">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
              กำลังส่งสัญญาณเสียงถึงปลายทาง... (รอรับสาย)
            </div>

            <div className="flex items-center justify-center gap-4 z-10">
              <button
                type="button"
                onClick={handleEndActiveCall}
                className="p-3.5 sm:p-4 bg-rose-600 hover:bg-rose-500 text-white rounded-2xl border border-rose-500 transition shadow-xl shadow-rose-950/80 transform hover:scale-105 flex items-center gap-2 font-bold text-xs"
              >
                <PhoneOff size={20} /> ยกเลิกการโทร
              </button>
            </div>

          </div>
        </div>
      )}

      {/* 🟢 ACTIVE CONNECTED CALL ROOM MODAL */}
      {activeCallSession && (
        <div className="fixed inset-0 bg-slate-950/95 z-50 flex items-center justify-center p-4 backdrop-blur-lg animate-in fade-in duration-200">
          <div className="bg-slate-900 border-2 border-emerald-500/60 rounded-3xl p-6 sm:p-8 max-w-lg w-full text-center shadow-2xl flex flex-col items-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-emerald-600/10 to-transparent pointer-events-none"></div>

            <div className="flex items-center justify-between w-full mb-4 pb-3 border-b border-slate-800">
              <span className="bg-emerald-950 text-emerald-300 border border-emerald-700/80 px-3 py-1 rounded-full text-xs font-extrabold flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>
                {activeCallSession.type === 'video' ? '📹 วิดีโอคอลเชื่อมต่อแล้ว' : '📞 คุยสายด่วนเชื่อมต่อแล้ว'}
              </span>

              <span className="text-white font-mono text-sm font-black bg-slate-950 px-3 py-1 rounded-xl border border-slate-800">
                ⏱️ {formatTimer(callTimer)}
              </span>
            </div>

            {/* Microphone Permission & Audio Stream Info Banner */}
            <div className="w-full mb-4 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs flex items-center justify-between">
              {micPermissionGranted ? (
                <span className="text-emerald-400 font-bold flex items-center gap-1">
                  <Volume2 size={15} className="animate-pulse" /> {audioConnected ? 'เชื่อมต่อสายและสัญญาณเสียงเรียบร้อย' : `ส่งสัญญาณเสียงหา @${activeCallSession.peerUsername}`}
                </span>
              ) : (
                <span className="text-amber-400 font-bold flex items-center gap-1">
                  <VolumeX size={15} /> กำลังรอสิทธิ์ไมโครโฟน
                </span>
              )}
              <span className="text-[10px] text-purple-300 font-mono bg-purple-950 px-2 py-0.5 rounded border border-purple-800">STUN WebRTC</span>
            </div>

            {/* Video or Avatar Stream Box */}
            <div className="relative mb-6 w-full h-48 sm:h-56 rounded-2xl bg-slate-950 border border-slate-800 overflow-hidden flex items-center justify-center shadow-inner">
              {activeCallSession.type === 'video' && !isVideoOff ? (
                <div className="w-full h-full flex flex-col items-center justify-center bg-slate-950 text-purple-300 p-4">
                  <Video size={48} className="animate-pulse mb-2 text-purple-400" />
                  <span className="text-xs font-bold text-slate-300">สัญญาณวิดีโอคอลสด (Live Video Stream)</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-purple-950 border-2 border-purple-500 overflow-hidden flex items-center justify-center text-white font-black text-2xl shadow-xl mb-2 relative">
                    {userAvatars[activeCallSession.peerUsername] ? (
                      <img src={userAvatars[activeCallSession.peerUsername]} alt={activeCallSession.peerName} className="w-full h-full object-cover" />
                    ) : (
                      activeCallSession.peerUsername.charAt(0).toUpperCase()
                    )}
                    <span className="absolute -bottom-1 -right-1 bg-emerald-500 w-4 h-4 rounded-full border-2 border-slate-950 animate-ping"></span>
                  </div>
                  <div className="flex items-center gap-1.5 text-emerald-400 text-xs font-bold mt-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    กำลังรับฟังเสียงจาก @{activeCallSession.peerUsername}
                  </div>
                </div>
              )}
            </div>

            <h3 className="text-xl sm:text-2xl font-black text-white mb-1">
              {activeCallSession.peerName}
            </h3>
            <p className="text-purple-300 font-mono text-xs font-bold mb-6">@{activeCallSession.peerUsername}</p>

            {/* Active Call Control Buttons */}
            <div className="flex items-center justify-center gap-4 z-10">
              <button
                type="button"
                onClick={toggleMicMute}
                className={`p-3.5 sm:p-4 rounded-2xl border transition shadow-lg flex items-center gap-1.5 font-bold text-xs ${
                  isMuted 
                    ? 'bg-rose-600 text-white border-rose-500' 
                    : 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700'
                }`}
                title={isMuted ? 'เปิดไมค์' : 'ปิดไมค์'}
              >
                {isMuted ? <MicOff size={20} /> : <Mic size={20} />}
                <span>{isMuted ? 'เปิดไมค์' : 'ปิดไมค์'}</span>
              </button>

              {activeCallSession.type === 'video' && (
                <button
                  type="button"
                  onClick={() => setIsVideoOff(!isVideoOff)}
                  className={`p-3.5 sm:p-4 rounded-2xl border transition shadow-lg flex items-center gap-1.5 font-bold text-xs ${
                    isVideoOff 
                      ? 'bg-rose-600 text-white border-rose-500' 
                      : 'bg-slate-800 hover:bg-slate-700 text-slate-200 border-slate-700'
                  }`}
                  title={isVideoOff ? 'เปิดกล้อง' : 'ปิดกล้อง'}
                >
                  {isVideoOff ? <VideoOff size={20} /> : <Video size={20} />}
                  <span>{isVideoOff ? 'เปิดกล้อง' : 'ปิดกล้อง'}</span>
                </button>
              )}

              <button
                type="button"
                onClick={handleEndActiveCall}
                className="p-3.5 sm:p-4 bg-rose-600 hover:bg-rose-500 text-white rounded-2xl border border-rose-500 transition shadow-xl shadow-rose-950/80 transform hover:scale-105 flex items-center gap-1.5 font-bold text-xs"
                title="วางสาย"
              >
                <PhoneOff size={20} />
                <span>วางสาย</span>
              </button>
            </div>

          </div>
        </div>
      )}

      {/* 🖼️ LIGHTBOX MODAL */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 backdrop-blur-md cursor-pointer"
          onClick={() => setLightboxImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img src={lightboxImage} alt="Expanded Preview" className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl border border-slate-700" />
            <button 
              onClick={() => setLightboxImage(null)}
              className="absolute -top-4 -right-4 bg-slate-800 text-white p-2 rounded-full border border-slate-600 hover:bg-rose-600 transition"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

    {/* 📊 KPI RESPONSE TIME LEADERBOARD MODAL */}
      {showKpiModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-3 sm:p-4 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-4xl w-full overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
            <div className="p-4 bg-gradient-to-r from-rose-950/80 via-slate-900 to-purple-950/80 border-b border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="text-base sm:text-lg font-extrabold text-white flex items-center gap-2">
                  📊 ตารางประเมินผล KPI การตอบแชทของทีม (Chat SLA & KPI Tracking)
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  เกณฑ์ประเมิน: ตอบภายใน 5 นาที = 100 คะแนน (ดีเยี่ยม) | 5–15 นาที = 80 คะแนน | เกิน 15 นาที หรือดองแชท = 40 คะแนน (หัก KPI)
                </p>
              </div>
              <button
                onClick={() => setShowKpiModal(false)}
                className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4 overflow-y-auto">
              <div className="overflow-x-auto border border-slate-800 rounded-xl">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-950 text-slate-300 border-b border-slate-800">
                      <th className="py-3 px-3 font-bold">พนักงาน</th>
                      <th className="py-3 px-3 font-bold text-center">ข้อความถึงทั้งหมด</th>
                      <th className="py-3 px-3 font-bold text-center text-emerald-400">⚡ ตอบไว (&le;5น.)</th>
                      <th className="py-3 px-3 font-bold text-center text-amber-300">⏱️ ปกติ (5-15น.)</th>
                      <th className="py-3 px-3 font-bold text-center text-rose-400">🚨 ตอบช้า (&gt;15น.)</th>
                      <th className="py-3 px-3 font-bold text-center">ค้างตอบ</th>
                      <th className="py-3 px-3 font-bold text-center">เวลาตอบเฉลี่ย</th>
                      <th className="py-3 px-3 font-bold text-center">คะแนน KPI</th>
                      <th className="py-3 px-3 font-bold text-center">เกรด / สถานะ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/70">
                    {(userList || [])
                      .filter(u => u && u.username !== 'ทุกคน')
                      .map((u, i) => {
                        const st = getUserKpiStats(u.username);
                        return (
                          <tr key={i} className="hover:bg-slate-800/40 transition">
                            <td className="py-2.5 px-3 font-bold text-white">
                              <div>{u.name}</div>
                              <div className="text-[10px] text-slate-400 font-mono">@{u.username}</div>
                            </td>
                            <td className="py-2.5 px-3 text-center font-bold text-slate-200">{st.totalAssigned}</td>
                            <td className="py-2.5 px-3 text-center font-bold text-emerald-400">{st.fastCount}</td>
                            <td className="py-2.5 px-3 text-center font-bold text-amber-300">{st.normalCount}</td>
                            <td className="py-2.5 px-3 text-center font-bold text-rose-400">{st.slowCount}</td>
                            <td className="py-2.5 px-3 text-center">
                              {st.pendingCount > 0 ? (
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
                                  st.pendingOverdueCount > 0 ? 'bg-rose-600 text-white animate-pulse' : 'bg-amber-500/20 text-amber-300'
                                }`}>
                                  {st.pendingCount} รายการ
                                </span>
                              ) : (
                                <span className="text-slate-500">0</span>
                              )}
                            </td>
                            <td className="py-2.5 px-3 text-center text-slate-200 font-mono">
                              {st.repliedCount > 0 ? formatDurationShort(st.avgSec) : '-'}
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <span className={`px-2.5 py-1 rounded-lg font-extrabold text-xs ${
                                st.kpiScore >= 90
                                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                                  : st.kpiScore >= 75
                                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                                  : 'bg-rose-600/30 text-rose-300 border border-rose-500/50'
                              }`}>
                                {st.kpiScore}%
                              </span>
                            </td>
                            <td className="py-2.5 px-3 text-center">
                              <span className={`px-2 py-0.5 rounded font-extrabold text-[11px] ${
                                st.grade === 'A'
                                  ? 'text-emerald-400'
                                  : st.grade === 'B'
                                  ? 'text-amber-300'
                                  : 'text-rose-400'
                              }`}>
                                {st.grade === 'A' ? 'เกรด A (ดีเยี่ยม)' : st.grade === 'B' ? 'เกรด B (ผ่านเกณฑ์)' : '⚠️ หัก KPI (ตอบช้า)'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="p-3 bg-slate-950 border-t border-slate-800 flex justify-end">
              <button
                onClick={() => setShowKpiModal(false)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-bold transition cursor-pointer"
              >
                ปิดหน้าต่าง
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
  );
}
